import React, { useState, useEffect, useRef } from "react";
import OtpInput from "../Login/utils/OtpInput";
import { validateOtp } from "../Login/utils/validate";
import { userAPI } from "../../../services/api";

import { useTranslation } from "react-i18next";

const RemovePin = ({ onRemovePin, onCancel, loading = false }) => {

  const { t } = useTranslation("auth");
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({ otp: "" });
  const [errors, setErrors] = useState({});
  const [otpSending, setOtpSending] = useState(false);

  const otpSentRef = useRef(false);

  useEffect(() => {
    if (!otpSentRef.current) {
      sendInternalOTP();
    }
  }, []);

  const sendInternalOTP = async () => {
    if (otpSending || otpSentRef.current) return;

    setOtpSending(true);
    otpSentRef.current = true;

    try {
      const response = await userAPI.resetPincodeInit();
      const { sended } = response.data
      if (sended === true) {
        setStep(2);
        setErrors({});
      } else {
        setErrors({ general: t("SMS_SEND_ERR") });
        otpSentRef.current = false;
      }
    } catch (err) {
      setErrors({ general: t("SMS_SEND_ERR") });
      otpSentRef.current = false;
    } finally {
      setOtpSending(false);
    }
  };

  const handleOtpChange = (value) => {
    setFormData({ otp: value });
    if (errors.otp) {
      setErrors((prev) => ({ ...prev, otp: "" }));
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();

    const otpErrors = validateOtp(formData.otp);
    if (Object.keys(otpErrors).length > 0) {
      setErrors(otpErrors);
      return;
    }

    const dataToSend = { otp: formData.otp.replace(/\D/g, "") };

    if (onRemovePin) {
      onRemovePin({ type: "confirm_remove", data: dataToSend });
    }
  };

  const handleResendOTP = async () => {
    otpSentRef.current = false;
    setFormData({ otp: "" });
    setErrors({});
    await userAPI.resetPincodeInit();
  };

  return (
    <div className="fixed inset-0 bg-white flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md mx-auto shadow-2xl animate-fadeIn relative">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 
                  2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 
                  0L3.732 16.5c-.77.833.192 2.5 
                  1.732 2.5z"
              />
            </svg>
          </div>

          {step === 1 && (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-3">
                {t("SENDING_SMS")}
              </h1>
              <p className="text-gray-500">
                {t("WE_SENDING_SMS")}
              </p>
            </>
          )}

          {step === 2 && (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-3">
                {t("CONFIRM_DELETE")}
              </h1>
              <p className="text-gray-500">
                {t("ENTER_CODE_TO_DELETE")}
              </p>
              {errors.general && (
                <p className="text-red-500 text-sm mt-2">{errors.general}</p>
              )}
            </>
          )}
        </div>

        <div className="flex flex-col gap-6">
          {step === 1 && (
            <div className="text-center py-8">
              <div className="w-16 h-16 border-4 border-red-200 border-t-red-600 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600 font-medium">
                {otpSending ? t("IS_SENDING_SMS") : t("GETTING_READY")}...
              </p>
            </div>
          )}

          {step === 2 && (
            <>
              <OtpInput
                value={formData.otp}
                onChange={handleOtpChange}
                error={errors.otp}
                disabled={loading}
              />

              <button
                onClick={handleOtpSubmit}
                disabled={loading}
                className="w-full bg-red-500 text-white py-4 rounded-xl font-semibold text-lg transition 
                           hover:bg-red-600 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-5 h-5 border-2 border-transparent border-t-white rounded-full animate-spin"></div>
                    {t("DELETING")}...
                  </div>
                ) : (
                  t("DELETE_CODE")
                )}
              </button>

              <button
                onClick={handleResendOTP}
                disabled={loading || otpSending}
                className="w-full text-red-500 font-medium py-3 hover:text-red-600 disabled:opacity-60"
              >
                {otpSending ? t("SENDING") + '...' : '↻' + t("RESEND")}
              </button>
            </>
          )}

          <button
            onClick={onCancel}
            disabled={loading}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-xl disabled:opacity-60 transition-all duration-200"
          >
            {t("CANCEL")}
          </button>
        </div>

        <div className="bg-red-50 rounded-lg p-4 mt-6">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 
                  4h13.856c1.54 0 2.502-1.667 
                  1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 
                  0L3.732 16.5c-.77.833.192 2.5 
                  1.732 2.5z"
              />
            </svg>
            <div>
              <div className="text-sm font-medium text-red-900 mb-1">
                {t("WARNING")}!
              </div>
              <div className="text-xs text-red-700">
                {t("DELETE_PIN_MSG")}.
              </div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-90 rounded-2xl flex items-center justify-center">
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 border-3 border-red-200 border-t-red-600 rounded-full animate-spin mb-4"></div>
              <p className="text-gray-600 font-medium">{t("DELETING_CODE")}...</p>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default RemovePin;

